import { Stack, Typography, useTheme } from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';

type CategoryGroup = {
	secondaryOrder: string[];
	items: Map<string, string[]>;
};

type PaymentCategoryDisplayProps = {
	primOrder: string[];
	groups: Map<string, CategoryGroup>;
};

export default function PaymentCategoryDisplay({ primOrder, groups }: PaymentCategoryDisplayProps) {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);

	const renderTertiaryItems = (items: string[], primaryKey: string, secondaryKey: string) => {
		return items.map((item, idx) => (
			<Typography
				key={`${primaryKey}-${secondaryKey}-item-${idx}`}
				variant="body2"
				color={theme.palette.root.basicDeepGreen}
			>
				• {t(`payment_category.name.${item}`, item)}
			</Typography>
		));
	};

	const renderSecondaryCategory = (
		secondaryKey: string,
		primaryKey: string,
		group: CategoryGroup
	) => {
		const tertiaryItems = group.items.get(secondaryKey) || [];

		return (
			<Stack key={`${primaryKey}-${secondaryKey}`} spacing={0.4} pl={1}>
				{secondaryKey !== primaryKey && (
					<Typography variant="subtitle2" color={theme.palette.root.basicDeepGreen}>
						{t(`payment_category.name.${secondaryKey}`, secondaryKey)}
					</Typography>
				)}
				{renderTertiaryItems(tertiaryItems, primaryKey, secondaryKey)}
			</Stack>
		);
	};

	const renderPrimaryCategory = (primaryKey: string) => {
		const group = groups.get(primaryKey);
		if (!group) return null;

		return (
			<Stack key={primaryKey} spacing={0.75}>
				<Typography
					variant="subtitle1"
					fontWeight={600}
					color={theme.palette.root.basicDeepGreen}
				>
					{t(`payment_category.name.${primaryKey}`, primaryKey)}
				</Typography>
				{group.secondaryOrder.map((secondaryKey) =>
					renderSecondaryCategory(secondaryKey, primaryKey, group)
				)}
			</Stack>
		);
	};

	return (
		<Stack gap={2} pb={2}>
			{primOrder.map((primaryKey) => renderPrimaryCategory(primaryKey))}
		</Stack>
	);
}

